import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { registerServiceWorker, applyUpdate } from '@/swRegistration';
import UpdateNotification from '@/components/UpdateNotification';
import { TeamProvider } from '@/contexts/TeamContext';

// Remove platform badge
function useBadgeRemoval() {
  useEffect(() => {
    const remove = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.parentNode.removeChild(el);
      document.querySelectorAll('a[href*="emergent-badge"]').forEach(n => n.parentNode.removeChild(n));
    };
    remove();
    const interval = setInterval(remove, 500);
    return () => clearInterval(interval);
  }, []);
}

import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import Tasks from '@/pages/Tasks';
import Documents from '@/pages/Documents';
import AIAssistant from '@/pages/AIAssistant';
import Templates from '@/pages/Templates';
import Matters from '@/pages/Matters';
import MatterDetail from '@/pages/MatterDetail';
import Settings from '@/pages/Settings';
import Inbox from '@/pages/Inbox';
import LegalCalendar from '@/pages/LegalCalendar';
import TeamCreate from '@/pages/TeamCreate';
import TeamSettings from '@/pages/TeamSettings';
import JoinTeam from '@/pages/JoinTeam';
import TeamActivity from '@/pages/TeamActivity';
import TeamDeadlines from '@/pages/TeamDeadlines';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import ProtectedRoute from '@/components/ProtectedRoute';
import Footer from '@/components/Footer';

function AppRouter() {
  const location = useLocation();
  useBadgeRemoval();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
      <Route path="/matters" element={<ProtectedRoute><Matters /></ProtectedRoute>} />
      <Route path="/matters/:matterId" element={<ProtectedRoute><MatterDetail /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
      <Route path="/legal-calendar" element={<ProtectedRoute><LegalCalendar /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/team/create" element={<ProtectedRoute><TeamCreate /></ProtectedRoute>} />
      <Route path="/team/settings" element={<ProtectedRoute><TeamSettings /></ProtectedRoute>} />
      <Route path="/team/activity" element={<ProtectedRoute><TeamActivity /></ProtectedRoute>} />
      <Route path="/team/deadlines" element={<ProtectedRoute><TeamDeadlines /></ProtectedRoute>} />
      <Route path="/join/:token" element={<JoinTeam />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  useEffect(() => {
    registerServiceWorker((registration) => {
      setSwRegistration(registration);
      setUpdateAvailable(true);
    });
  }, []);

  const handleUpdate = () => {
    applyUpdate(swRegistration);
    setUpdateAvailable(false);
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <TeamProvider>
          <div className="flex-1">
            <AppRouter />
          </div>
          <Footer />
        </TeamProvider>
      </BrowserRouter>
      <UpdateNotification show={updateAvailable} onUpdate={handleUpdate} />
    </div>
  );
}

export default App;
